import { useState, useEffect, useCallback } from 'react';
import type { FolderApi, Folder } from '../services/folder.service';
import { fetchFolderChildren } from '../services/folder.service';

/**
 * Recursively build out the tree by fetching children for each FolderApi node.
 */
async function buildTree(nodes: FolderApi[] = []): Promise<Folder[]> {
  const tree = await Promise.all(
    nodes.map(async (node) => {
      const children = await fetchFolderChildren(node.folderId);
      const subfolders = children.length > 0 ? await buildTree(children) : [];
      return { ...node, folders: subfolders };
    })
  );
  return tree;
}

/**
 * useFolderTree fetches the entire nested folder tree.
 * Returns an object with `folders` (the tree), `loading` flag, and a `refetchFolders` function.
 */
export function useFolderTree() {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchTreeData = useCallback(async (setLoadingState = true) => {
    if (setLoadingState) setLoading(true);
    try {
      const roots = await fetchFolderChildren(0);
      const tree = await buildTree(roots);
      setFolders(tree);
    } catch (error) {
      console.error('Error loading folder tree', error);
      setFolders([]);
    } finally {
      if (setLoadingState) setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchTreeData().finally(() => {
      if (mounted) {
        // No need to set loading false again here, fetchTreeData handles it.
      }
    });
    return () => {
      mounted = false;
    };
  }, [fetchTreeData]);

  return { folders, loading, refetchFolders: fetchTreeData };
} 